/**
 * **Race portraits for the menu — panel portraits, not unit sprites.**
 *
 * Drawn the way a Renaissance medal or a donor panel draws a ruler: **head in
 * strict profile**, shoulders turned a little towards you, the sitter set back
 * from the picture edge with the empty space *in front of the face* rather than
 * behind it. That last rule is why each bust sits left of centre — a profile
 * looking into a wall reads as a mugshot, and a profile with air ahead of it
 * reads as a portrait.
 *
 * They are deliberately not the map sprites. A unit seen from above at eight
 * pixels is a readable soldier and an unreadable face; a portrait wants a brow,
 * a nose and one silhouette cue you can name in a quarter of a second — the
 * human's crown, the dwarf's beard, the elf's ear.
 *
 * 24 × 24 rather than the sprites' 16, because a profile needs room for a nose
 * and a jaw, and the same `PAL` palette as everything else in `render/art/` so
 * three portraits and forty sprites stay one family of colour. Rasterised at
 * boot by `racePortraitUrl` — no files, nothing to fetch.
 *
 * `P` is the tinted layer, as always; here it is unused, because a portrait
 * belongs to a people rather than to a kingdom and the robes are painted in
 * their own colours.
 *
 * **Every row must be exactly `PORTRAIT_W` characters.** A short row is padded
 * with blanks and a long one is clipped to the width of row 0, so a miscount
 * shows up as a crooked face rather than as an error. `server/checks.ts`
 * measures them.
 */

export const PORTRAIT_W = 24;

/** Indexed by `Race`: 0 Human, 1 Dwarf, 2 Elf. */
export const ART_RACE_PORTRAITS: string[][] = [
  /* Human — a crowned head in profile over a crimson robe with a gold band */
  [
    '                        ',
    '       y y y y          ',
    '       yyyyyyy          ',
    '      #yyyyyyy#         ',
    '     #wwwwwwwwww#       ',
    '    #wwwwwwwwss#        ',
    '    #wwwwwwsssss#       ',
    '    #wwwwwssksss#       ',
    '    #wwwwwsssssss#      ',
    '    #wwwwwssssssss#     ',
    '    #wwwwwsssssss#      ',
    '    #wwwwwssss##        ',
    '    #wwwwwsssss#        ',
    '     #wwwwssss#         ',
    '      #wwwsss#          ',
    '       #ssss#           ',
    '      #ssssss#          ',
    '    #xxxxxxxxxx#        ',
    '   #xxxxyyyyxxxx#       ',
    '  #xxxxxyyyyxxxxx#      ',
    ' #xxxxxxyyyyxxxxxx#     ',
    ' #xxxxxxyyyyxxxxxx#     ',
    ' #xxxxxxyyyyxxxxxx#     ',
    ' #xxxxxxxxxxxxxxxx#     ',
  ],
  /* Dwarf — a helm with a brim, and the beard doing most of the work */
  [
    '                        ',
    '     ########           ',
    '    #dddddddd#          ',
    '   #dddddddddd#         ',
    '   #mmmmmmmmmm#         ',
    '    #wwwwwsssss#        ',
    '    #wwwwsskssss#       ',
    '    #wwwwssssssss#      ',
    '    #wwwwsssssss#       ',
    '    #wwwwwwwwww#        ',
    '   #wwwwwwwwwwww#       ',
    '   #wwwwwwwwwwww#       ',
    '   #wwwwwwwwwwww#       ',
    '   #wwwwwwwwwww#        ',
    '    #wwwwwwwww#         ',
    '     #wwwwwww#          ',
    '      #wwwww#           ',
    '    #qqqqqqqqqq#        ',
    '   #qqqqnnnnqqqq#       ',
    '  #qqqqqnnnnqqqqq#      ',
    ' #qqqqqqnnnnqqqqqq#     ',
    ' #qqqqqqnnnnqqqqqq#     ',
    ' #qqqqqqnnnnqqqqqq#     ',
    ' #qqqqqqqqqqqqqqqq#     ',
  ],
  /* Elf — a circlet, a pointed ear, pale hair past the jaw, green mantle */
  [
    '                        ',
    '      NNNNNN            ',
    '     #NNNNNNN#          ',
    '    #NNNNNNNNy#         ',
    '    #NNNNNNNss#         ',
    '    #NNNNNsssss#        ',
    '    #NNNNsksssss#       ',
    '  s #NNNNsssssss#       ',
    '   s#NNNNssssss#        ',
    '    #NNNNssss##         ',
    '    #NNNNsssss#         ',
    '     #NNNsss#           ',
    '      #NNsss#           ',
    '      #Nssss#           ',
    '       #ssss#           ',
    '      #Nssss#           ',
    '     #NNssss#           ',
    '    #GggggggG#          ',
    '   #GgggyyggggG#        ',
    '  #GggggyygggggG#       ',
    ' #GgggggyyggggggG#      ',
    ' #GgggggyyggggggG#      ',
    ' #GgggggyyggggggG#      ',
    ' #GGGGGGGGGGGGGGG#      ',
  ],
];

/**
 * The field each portrait sits on, and the colour its `P` pixels would take.
 *
 * Three different colours on purpose: three busts of the same size on the same
 * background are three grey blobs at a glance, and the plate is what tells you
 * which card is which before you have focused on a face.
 *
 * **Each plate is chosen to fight its own portrait, not to match it.** The
 * first pass used a brown field for the human and a steel one for the dwarf —
 * the colours you would pick if you were decorating — and measuring the
 * rendered pixels showed exactly what that does: brown hair on brown, and a
 * steel helm on steel, close enough in hue *and* luminance to vanish. The
 * sitter has to be the light thing on a dark field of another hue.
 */
export const PORTRAIT_PLATE = ['#26303d', '#33262e', '#25361f'];
export const PORTRAIT_TINT = ['#e6d5a8', '#c88a4a', '#a8d18a'];
